/**
 * Namespaced, versioned, fail-safe localStorage wrapper.
 *
 * All reads are guarded because storage can be unavailable (Safari private
 * mode, disabled cookies) or corrupted. Callers never see a thrown error.
 */
const NAMESPACE = 'agave';
const VERSION = 'v1';

function key(name: string): string {
  return `${NAMESPACE}:${VERSION}:${name}`;
}

function available(): boolean {
  try {
    const probe = '__agave_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

const canPersist = typeof window !== 'undefined' && available();

export function loadState<T>(name: string, fallback: T): T {
  if (!canPersist) return fallback;
  try {
    const raw = window.localStorage.getItem(key(name));
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function saveState<T>(name: string, value: T): void {
  if (!canPersist) return;
  try {
    window.localStorage.setItem(key(name), JSON.stringify(value));
  } catch {
    /* quota exceeded or storage disabled — non-fatal */
  }
}

export function removeState(name: string): void {
  if (!canPersist) return;
  try {
    window.localStorage.removeItem(key(name));
  } catch {
    /* ignore */
  }
}

/** Wipe every Agave-owned key (used on logout / account reset). */
export function clearAllState(): void {
  if (!canPersist) return;
  try {
    const prefix = `${NAMESPACE}:${VERSION}:`;
    const victims: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k?.startsWith(prefix)) victims.push(k);
    }
    victims.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

/** Session-scoped storage (cleared when the tab closes). */
export function loadSession<T>(name: string, fallback: T): T {
  try {
    const raw = window.sessionStorage.getItem(key(name));
    return raw == null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function saveSession<T>(name: string, value: T): void {
  try {
    window.sessionStorage.setItem(key(name), JSON.stringify(value));
  } catch {
    /* ignore */
  }
}
